import './appIcon.css'

/**
 * ICONITA UNEI APLICATII: O SINGURA FATA, SCRISA INTR-UN SINGUR LOC.
 *
 * Fisierul asta exista fiindca doi oameni au judecat acelasi lucru separat si
 * au ajuns la doua raspunsuri. Alesul de iconite din fila Settings pornise de la
 * „cartonasul deseneaza cu fata OUTLINE, deci ofera numai ce e in ea" - 30 de
 * iconite - iar reparatia cartonasului pornise de la celalalt capat: „fata
 * outline era gresita de la bun inceput". A doua are dreptate, si de-aia nu mai
 * sta nicaieri de doua ori: si cartonasul, si alesul, cer clasa de aici.
 *
 * ---------------------------------------------------------------------------
 * CARE E FATA, SI DE CE
 * ---------------------------------------------------------------------------
 *
 * Butonul cu care omul isi alege iconita in portal e `bootstrap-iconpicker` cu
 * `iconset: 'fontawesome'` (`settings-1.js`), adica setul Font Awesome 4.7 din
 * `bootstrap-iconpicker-iconset-all.js`, unde scrie `iconClass: 'fa'`. Deci
 * previzualizarile din alesul ala sunt desenate cu `.fa` - care in Font Awesome
 * 5 inseamna fata SOLID, greutatea 900. Omul a ales o iconita plina.
 *
 * `far` inseamna aceeasi familie la greutatea 400, adica fata REGULAR: 151 de
 * iconite cu contur, atat are varianta gratuita. Cartonasul desena cu ea, deci
 * cerea din fisierul gresit ce i se aratase omului din celalalt.
 *
 * MASURAT in Edge, la 90px, pe panza, cu foile adevarate servite pe HTTP,
 * numarand pixelii de cerneala ai fiecarei glife:
 *
 *     nume                 far    fas     fa    fa + regula de mai jos
 *     fa-database            0   6009   6009    6009
 *     fa-server              0   6064   6064    6064
 *     fa-lock                0   5431   5431    5431
 *     fa-hdd              3482   5496   5496    5496
 *     fa-envelope         3422   5656   5656    5656
 *     fa-windows             0      0      0    5863
 *     fa-linux               0      0      0    2936
 *
 * Zero inseamna ca elementul e acolo si nu deseneaza nimic. `fa-database` -
 * chiar valoarea implicita a portalului - era pe zero. Cele care se vedeau
 * totusi (hdd, envelope) se vedeau fiindca numele lor e in amandoua fisierele.
 *
 * Ultima coloana e a treia bucata: marcile (`fa-windows`, `fa-linux`,
 * `fa-apple`, `fa-chrome`) au fost mutate de Font Awesome 5 intr-o familie
 * separata, pe care n-o cheama nici `fa` nici `fas`. Coada de familii din
 * `appIcon.css` le prinde, si se vede ca lucreaza si la greutatea 900.
 *
 * ---------------------------------------------------------------------------
 * LISTA: CE OFEREA ALESUL PORTALULUI, FARA CE NU MAI EXISTA
 * ---------------------------------------------------------------------------
 *
 * Cele 785 de nume din setul Font Awesome 4.7 al lui `bootstrap-iconpicker` -
 * exact ce putea alege omul in portal - trecute fiecare prin aceeasi masuratoare
 * sub regula de mai jos:
 *
 *     desenate                                 519
 *     fara glifa, desi au clasa                  0
 *     fara clasa in foaia Font Awesome 5       266
 *
 * Cele 266 sunt numele pe care Font Awesome 5 le-a redenumit sau le-a scos:
 * toate variantele `-o` (conturul a devenit `far fa-...`), `fa-bar-chart`
 * (acum `fa-chart-bar`), poreclele ca `fa-automobile` sau `fa-bank`. Alea n-au
 * clasa in nicio foaie pe care o incarcam, deci n-ar desena nimic si nu se
 * ofera. Zeroul din mijloc e cel care conteaza: sub regula asta, TOT ce mai are
 * clasa chiar are si glifa - nu ramane nicio gaura.
 */

/** Valoarea implicita a portalului (`settings.php`, si cartonasul). */
export const DEFAULT_APP_ICON = 'fa-database'

/**
 * Fata, fara numele iconitei.
 *
 * `fa` e chiar clasa cu care i s-a aratat omului iconita cand a ales-o;
 * `core-app-icon` aduce coada de familii din `appIcon.css`, pentru marci.
 *
 * Se da si asa fiindca `BoardCard` primeste numele si fata pe doua intrari
 * separate (`icon`, `iconFace`) - isi pune el clasele pe placuta - pe cand
 * alesul isi scrie elementul singur si cere `appIconClass`. Doua intrari, un
 * singur sir, scris o data.
 */
export const APP_ICON_FACE = 'fa core-app-icon'

/**
 * Clasele cu care se deseneaza iconita unei aplicatii - si pe cartonasul din
 * Core Applications Dashboard, si pe fiecare buton din alesul filei Settings.
 */
export function appIconClass(icon?: string | null): string {
  const name = icon === null || icon === undefined || icon.trim() === '' ? DEFAULT_APP_ICON : icon.trim()

  return `${APP_ICON_FACE} ${name}`
}

/** Cele 519 masurate. Vezi capul fisierului pentru de unde vin si cum. */
export const APP_ICONS: string[] = [
  'fa-500px', 'fa-address-book', 'fa-address-card', 'fa-adjust', 'fa-adn', 'fa-align-center',
  'fa-align-justify', 'fa-align-left', 'fa-align-right', 'fa-amazon', 'fa-ambulance', 'fa-american-sign-language-interpreting',
  'fa-anchor', 'fa-android', 'fa-angellist', 'fa-angle-double-down', 'fa-angle-double-left', 'fa-angle-double-right',
  'fa-angle-double-up', 'fa-angle-down', 'fa-angle-left', 'fa-angle-right', 'fa-angle-up', 'fa-apple',
  'fa-archive', 'fa-arrow-circle-down', 'fa-arrow-circle-left', 'fa-arrow-circle-right', 'fa-arrow-circle-up', 'fa-arrow-down',
  'fa-arrow-left', 'fa-arrow-right', 'fa-arrow-up', 'fa-arrows-alt', 'fa-assistive-listening-systems', 'fa-asterisk',
  'fa-at', 'fa-audio-description', 'fa-backward', 'fa-balance-scale', 'fa-ban', 'fa-bandcamp',
  'fa-barcode', 'fa-bars', 'fa-bath', 'fa-battery-empty', 'fa-battery-full', 'fa-battery-half',
  'fa-battery-quarter', 'fa-battery-three-quarters', 'fa-bed', 'fa-beer', 'fa-behance', 'fa-behance-square',
  'fa-bell', 'fa-bell-slash', 'fa-bicycle', 'fa-binoculars', 'fa-birthday-cake', 'fa-bitbucket',
  'fa-bitcoin', 'fa-black-tie', 'fa-blind', 'fa-bluetooth', 'fa-bluetooth-b', 'fa-bold',
  'fa-bolt', 'fa-bomb', 'fa-book', 'fa-bookmark', 'fa-braille', 'fa-briefcase',
  'fa-btc', 'fa-bug', 'fa-building', 'fa-bullhorn', 'fa-bullseye', 'fa-bus',
  'fa-buysellads', 'fa-calculator', 'fa-calendar', 'fa-camera', 'fa-camera-retro', 'fa-car',
  'fa-caret-down', 'fa-caret-left', 'fa-caret-right', 'fa-caret-up', 'fa-cart-arrow-down', 'fa-cart-plus',
  'fa-cc-amex', 'fa-cc-diners-club', 'fa-cc-discover', 'fa-cc-jcb', 'fa-cc-mastercard', 'fa-cc-paypal',
  'fa-cc-stripe', 'fa-cc-visa', 'fa-certificate', 'fa-check', 'fa-check-circle', 'fa-check-square',
  'fa-chevron-circle-down', 'fa-chevron-circle-left', 'fa-chevron-circle-right', 'fa-chevron-circle-up', 'fa-chevron-down', 'fa-chevron-left',
  'fa-chevron-right', 'fa-chevron-up', 'fa-child', 'fa-chrome', 'fa-circle', 'fa-clipboard',
  'fa-clone', 'fa-cloud', 'fa-code', 'fa-codepen', 'fa-codiepie', 'fa-coffee',
  'fa-cog', 'fa-cogs', 'fa-columns', 'fa-comment', 'fa-comments', 'fa-compass',
  'fa-compress', 'fa-connectdevelop', 'fa-contao', 'fa-copy', 'fa-copyright', 'fa-creative-commons',
  'fa-credit-card', 'fa-crop', 'fa-crosshairs', 'fa-css3', 'fa-cube', 'fa-cubes',
  'fa-cut', 'fa-dashcube', 'fa-database', 'fa-deaf', 'fa-delicious', 'fa-desktop',
  'fa-deviantart', 'fa-digg', 'fa-download', 'fa-dribbble', 'fa-dropbox', 'fa-drupal',
  'fa-edge', 'fa-edit', 'fa-eject', 'fa-ellipsis-h', 'fa-ellipsis-v', 'fa-empire',
  'fa-envelope', 'fa-envelope-open', 'fa-envelope-square', 'fa-envira', 'fa-eraser', 'fa-etsy',
  'fa-exclamation', 'fa-exclamation-circle', 'fa-exclamation-triangle', 'fa-expand', 'fa-expeditedssl', 'fa-eye',
  'fa-eye-slash', 'fa-facebook', 'fa-facebook-f', 'fa-facebook-square', 'fa-fast-backward', 'fa-fast-forward',
  'fa-fax', 'fa-female', 'fa-fighter-jet', 'fa-file', 'fa-film', 'fa-filter',
  'fa-fire', 'fa-fire-extinguisher', 'fa-firefox', 'fa-first-order', 'fa-flag', 'fa-flag-checkered',
  'fa-flask', 'fa-flickr', 'fa-folder', 'fa-folder-open', 'fa-font', 'fa-font-awesome',
  'fa-fonticons', 'fa-fort-awesome', 'fa-forumbee', 'fa-forward', 'fa-foursquare', 'fa-free-code-camp',
  'fa-gamepad', 'fa-gavel', 'fa-genderless', 'fa-get-pocket', 'fa-gg', 'fa-gg-circle',
  'fa-gift', 'fa-git', 'fa-git-square', 'fa-github', 'fa-github-alt', 'fa-github-square',
  'fa-gitlab', 'fa-glide', 'fa-glide-g', 'fa-globe', 'fa-google', 'fa-google-plus',
  'fa-google-plus-square', 'fa-google-wallet', 'fa-graduation-cap', 'fa-gratipay', 'fa-grav', 'fa-h-square',
  'fa-hacker-news', 'fa-hashtag', 'fa-headphones', 'fa-heart', 'fa-heartbeat', 'fa-history',
  'fa-home', 'fa-hotel', 'fa-hourglass', 'fa-hourglass-end', 'fa-hourglass-half', 'fa-hourglass-start',
  'fa-houzz', 'fa-html5', 'fa-i-cursor', 'fa-id-badge', 'fa-id-card', 'fa-image',
  'fa-imdb', 'fa-inbox', 'fa-indent', 'fa-industry', 'fa-info', 'fa-info-circle',
  'fa-instagram', 'fa-internet-explorer', 'fa-ioxhost', 'fa-italic', 'fa-joomla', 'fa-jsfiddle',
  'fa-key', 'fa-language', 'fa-laptop', 'fa-lastfm', 'fa-lastfm-square', 'fa-leaf',
  'fa-leanpub', 'fa-life-ring', 'fa-link', 'fa-linkedin', 'fa-linode', 'fa-linux',
  'fa-list', 'fa-list-alt', 'fa-list-ol', 'fa-list-ul', 'fa-location-arrow', 'fa-lock',
  'fa-low-vision', 'fa-magic', 'fa-magnet', 'fa-male', 'fa-map', 'fa-map-marker',
  'fa-map-pin', 'fa-map-signs', 'fa-mars', 'fa-mars-double', 'fa-mars-stroke', 'fa-mars-stroke-h',
  'fa-mars-stroke-v', 'fa-maxcdn', 'fa-medium', 'fa-medkit', 'fa-meetup', 'fa-mercury',
  'fa-microchip', 'fa-microphone', 'fa-microphone-slash', 'fa-minus', 'fa-minus-circle', 'fa-minus-square',
  'fa-mixcloud', 'fa-mobile', 'fa-modx', 'fa-motorcycle', 'fa-mouse-pointer', 'fa-music',
  'fa-neuter', 'fa-object-group', 'fa-object-ungroup', 'fa-odnoklassniki', 'fa-odnoklassniki-square', 'fa-opencart',
  'fa-openid', 'fa-opera', 'fa-optin-monster', 'fa-outdent', 'fa-pagelines', 'fa-paint-brush',
  'fa-paper-plane', 'fa-paperclip', 'fa-paragraph', 'fa-paste', 'fa-pause', 'fa-pause-circle',
  'fa-paw', 'fa-paypal', 'fa-percent', 'fa-phone', 'fa-phone-square', 'fa-pied-piper',
  'fa-pied-piper-alt', 'fa-pied-piper-pp', 'fa-pinterest', 'fa-pinterest-p', 'fa-pinterest-square', 'fa-plane',
  'fa-play', 'fa-play-circle', 'fa-plug', 'fa-plus', 'fa-plus-circle', 'fa-plus-square',
  'fa-podcast', 'fa-power-off', 'fa-print', 'fa-product-hunt', 'fa-puzzle-piece', 'fa-qq',
  'fa-qrcode', 'fa-question', 'fa-question-circle', 'fa-quora', 'fa-quote-left', 'fa-quote-right',
  'fa-random', 'fa-ravelry', 'fa-rebel', 'fa-recycle', 'fa-reddit', 'fa-reddit-alien',
  'fa-reddit-square', 'fa-registered', 'fa-renren', 'fa-reply', 'fa-reply-all', 'fa-retweet',
  'fa-road', 'fa-rocket', 'fa-rss', 'fa-rss-square', 'fa-safari', 'fa-save',
  'fa-scribd', 'fa-search', 'fa-search-minus', 'fa-search-plus', 'fa-sellsy', 'fa-server',
  'fa-share', 'fa-share-alt', 'fa-share-alt-square', 'fa-share-square', 'fa-ship', 'fa-shirtsinbulk',
  'fa-shopping-bag', 'fa-shopping-basket', 'fa-shopping-cart', 'fa-shower', 'fa-sign-language', 'fa-signal',
  'fa-simplybuilt', 'fa-sitemap', 'fa-skyatlas', 'fa-skype', 'fa-slack', 'fa-slideshare',
  'fa-snapchat', 'fa-snapchat-ghost', 'fa-snapchat-square', 'fa-sort', 'fa-sort-down', 'fa-sort-up',
  'fa-soundcloud', 'fa-space-shuttle', 'fa-spinner', 'fa-spotify', 'fa-square', 'fa-stack-exchange',
  'fa-stack-overflow', 'fa-star', 'fa-star-half', 'fa-steam', 'fa-steam-square', 'fa-step-backward',
  'fa-step-forward', 'fa-stethoscope', 'fa-sticky-note', 'fa-stop', 'fa-stop-circle', 'fa-street-view',
  'fa-strikethrough', 'fa-stumbleupon', 'fa-stumbleupon-circle', 'fa-subscript', 'fa-subway', 'fa-suitcase',
  'fa-superpowers', 'fa-superscript', 'fa-table', 'fa-tablet', 'fa-tag', 'fa-tags',
  'fa-tasks', 'fa-taxi', 'fa-telegram', 'fa-tencent-weibo', 'fa-terminal', 'fa-text-height',
  'fa-text-width', 'fa-th', 'fa-th-large', 'fa-th-list', 'fa-themeisle', 'fa-thermometer',
  'fa-thermometer-empty', 'fa-thermometer-full', 'fa-thermometer-half', 'fa-thermometer-quarter', 'fa-thermometer-three-quarters', 'fa-thumbs-down',
  'fa-thumbs-up', 'fa-times', 'fa-times-circle', 'fa-tint', 'fa-toggle-off', 'fa-toggle-on',
  'fa-trademark', 'fa-train', 'fa-transgender', 'fa-transgender-alt', 'fa-trash', 'fa-tree',
  'fa-trello', 'fa-trophy', 'fa-truck', 'fa-tty', 'fa-tumblr', 'fa-tumblr-square',
  'fa-tv', 'fa-twitch', 'fa-twitter', 'fa-twitter-square', 'fa-umbrella', 'fa-underline',
  'fa-undo', 'fa-universal-access', 'fa-university', 'fa-unlink', 'fa-unlock', 'fa-unlock-alt',
  'fa-upload', 'fa-usb', 'fa-user', 'fa-user-circle', 'fa-user-md', 'fa-user-plus',
  'fa-user-secret', 'fa-user-times', 'fa-users', 'fa-venus', 'fa-venus-double', 'fa-venus-mars',
  'fa-viacoin', 'fa-viadeo', 'fa-viadeo-square', 'fa-vimeo', 'fa-vimeo-square', 'fa-vine',
  'fa-vk', 'fa-volume-down', 'fa-volume-off', 'fa-volume-up', 'fa-weibo', 'fa-weixin',
  'fa-whatsapp', 'fa-wheelchair', 'fa-wifi', 'fa-wikipedia-w', 'fa-window-close', 'fa-window-maximize',
  'fa-window-minimize', 'fa-window-restore', 'fa-windows', 'fa-wordpress', 'fa-wpbeginner', 'fa-wpexplorer',
  'fa-wpforms', 'fa-wrench', 'fa-xing', 'fa-xing-square', 'fa-y-combinator', 'fa-yahoo',
  'fa-yelp', 'fa-yoast', 'fa-youtube',
]

